import { Skeleton, useToast } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Routes, Navigate, Route } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { AssignmentEditRoute } from './AssignmentEditRoute';
import { AssignmentApplyStrategyRoute } from 'borrower/routes/assignment/AssignmentApplyStrategyRoute';
import { AssignmentEditByCSVRoute } from 'borrower/routes/assignment/AssignmentEditByCSVRoute';
import { AssignmentSelectStrategyRoute } from 'borrower/routes/assignment/AssignmentSelectStrategyRoute';
import { errorToastConfig } from 'packages/common-frontend/constants';
import CreditFacilityProvider from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { LatestAssignmentPipelineRunMetadataForLpGroupingProvider } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { LoanProductCreditFacilityGroupProvider } from 'packages/loanProduct/hooks/LoanProductCreditFacilityGroupingProvider';
import { AssignByIndividual } from 'packages/loanProduct/ui/v2/AssignByIndividual';
import { AssignmentDashboardContainer } from 'packages/loanProduct/ui/v2/AssignmentSummary/AssignmentDashboard';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const AssignmentRouter = () => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const { data, loading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });
  const isProduction = useMemo(() => commonUtils.EnvUtils.isProduction(), []);

  if (loading || !data || !data.user) {
    return <Skeleton />;
  }

  const { organizationId } = data.user;

  return (
    <CreditFacilityProvider
      renderLoading={<Skeleton />}
      organizationId={organizationId}
      fallbackComponent={<Navigate to="/" />}
    >
      {/* @jendevelops TODO move to route once cutover to v2 is complete */}
      <LoanProductCreditFacilityGroupProvider
        organizationId={organizationId}
        renderLoading={<Skeleton />}
        fallbackComponent={<Skeleton />}
      >
        <LatestAssignmentPipelineRunMetadataForLpGroupingProvider
          renderLoading={<Skeleton />}
        >
          {/* Paths relative to "/assignments/*" */}
          <Routes>
            <Route
              path="/summary/*"
              element={
                <AssignmentDashboardContainer organizationId={organizationId} />
              }
            />
            {!isProduction && (
              <Route
                path="/v2/edit/*"
                element={<AssignByIndividual organizationId={organizationId} />}
              />
            )}
            {flags?.assignByIndividualReceivable && (
              <Route path="/edit" element={<AssignmentEditRoute />} />
            )}
            <Route path="/edit-by-csv" element={<AssignmentEditByCSVRoute />} />
            <Route
              path="/strategy"
              element={<AssignmentSelectStrategyRoute />}
            />
            <Route
              path="/strategy/:strategyId/apply"
              element={<AssignmentApplyStrategyRoute />}
            />
            <Route path="/*" element={<Navigate to="/assignments/summary" />} />
          </Routes>
        </LatestAssignmentPipelineRunMetadataForLpGroupingProvider>
      </LoanProductCreditFacilityGroupProvider>
    </CreditFacilityProvider>
  );
};
